import {
  ArrowUpTrayIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { Button } from '@/components/ui/catalyst/button';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import type { Blob as BlobType } from '@/services/types/blob-types';
import type { CollectionNode } from '@/services/types/collection-types';

interface DocumentSelectorProps {
  matterId: string;
  selectedBlobIds: string[];
  onSelectionChange: (blobIds: string[]) => void;
  onConfirm: () => void;
  filterBlobs?: (blob: BlobType) => boolean;
  multiSelect?: boolean;
  className?: string;
  showConfirmButton?: boolean;
  enablePreview?: boolean;
  enableUpload?: boolean;
  previewDialogTitle?: string;
  /** File types to accept for upload */
  acceptFileTypes?: string;
}

type TabMode = 'select' | 'upload';

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  id: string;
  isRemoving?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
};

const getBlobDisplayName = (blob: BlobType): string =>
  blob.name || blob.original_filename || 'Unnamed document';

const getFileExtension = (blob: BlobType): string => {
  const filename = blob.original_filename || blob.name || '';
  return filename.split('.').pop()?.toUpperCase() || 'FILE';
};

const documentFilter = (blob: BlobType): boolean => {
  const filename = blob.original_filename || blob.name || '';
  return /\.(pdf|docx?|txt|jpe?g|png|gif|webp|tiff?|bmp)$/i.test(filename);
};

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  matterId,
  selectedBlobIds,
  onSelectionChange,
  onConfirm,
  filterBlobs = documentFilter,
  multiSelect = false,
  className = '',
  showConfirmButton = true,
  enablePreview = false,
  enableUpload = false,
  previewDialogTitle = 'Document Preview',
  acceptFileTypes = '.pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.webp',
}) => {
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });
  const {
    data: blobs,
    isLoading,
    refetch: refetchBlobs,
  } = useGetBlobsQuery(matterId, { skip: !matterId });

  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewBlob, setPreviewBlob] = useState<BlobType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const lastClickedIndexRef = useRef<number | null>(null);

  // Upload state
  const [activeTab, setActiveTab] = useState<TabMode>('select');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { handleUploadToCollectionAndReturn } = useUploadManagerUnified({
    matterId,
    refetch: refetchBlobs,
  });

  useEffect(() => {
    if (collectionTree && !selectedCollectionId) {
      setSelectedCollectionId(collectionTree.id);
    }
  }, [collectionTree, selectedCollectionId]);

  const filteredBlobs = useMemo(() => {
    if (!blobs) return [];
    return blobs.filter(
      (blob) =>
        filterBlobs(blob) &&
        (selectedCollectionId === collectionTree?.id ||
          blob.collection_details.id === selectedCollectionId) &&
        (searchQuery === '' ||
          blob.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          blob.original_filename
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())),
    );
  }, [blobs, selectedCollectionId, collectionTree, searchQuery, filterBlobs]);

  const selectedBlobs = useMemo(() => {
    if (!blobs) return [];
    return blobs.filter((blob) => selectedBlobIds.includes(blob.id));
  }, [blobs, selectedBlobIds]);

  const handleToggleSelect = useCallback(
    (blobId: string, index: number, shiftKey = false) => {
      if (multiSelect) {
        if (shiftKey && lastClickedIndexRef.current !== null) {
          const start = Math.min(lastClickedIndexRef.current, index);
          const end = Math.max(lastClickedIndexRef.current, index);
          const rangeIds = filteredBlobs.slice(start, end + 1).map((b) => b.id);
          const newSelection = new Set([...selectedBlobIds, ...rangeIds]);
          onSelectionChange(Array.from(newSelection));
        } else if (selectedBlobIds.includes(blobId)) {
          onSelectionChange(selectedBlobIds.filter((id) => id !== blobId));
          lastClickedIndexRef.current = index;
        } else {
          onSelectionChange([...selectedBlobIds, blobId]);
          lastClickedIndexRef.current = index;
        }
      } else {
        onSelectionChange(selectedBlobIds.includes(blobId) ? [] : [blobId]);
      }
    },
    [selectedBlobIds, onSelectionChange, multiSelect, filteredBlobs],
  );

  const handleRemoveSelection = useCallback(
    (blobId: string) => {
      onSelectionChange(selectedBlobIds.filter((id) => id !== blobId));
    },
    [selectedBlobIds, onSelectionChange],
  );

  const buildCollectionOptions = useCallback(
    (
      node: CollectionNode,
      level = 0,
    ): Array<{ value: string; label: string }> => {
      const options: Array<{ value: string; label: string }> = [];
      const prefix = '\u00A0\u00A0'.repeat(level);
      options.push({
        value: node.id,
        label: prefix + (node.name === 'root' ? 'All Files' : node.name),
      });
      if (node.children) {
        node.children.forEach((child: CollectionNode) => {
          options.push(...buildCollectionOptions(child, level + 1));
        });
      }
      return options;
    },
    [],
  );

  const collectionOptions = useMemo(() => {
    if (!collectionTree) return [];
    return buildCollectionOptions(collectionTree);
  }, [collectionTree, buildCollectionOptions]);

  // Upload handlers
  const handleUploadFiles = async (files: File[]) => {
    if (files.length === 0 || !selectedCollectionId) return;

    const newUploadedFiles: UploadedFile[] = files.map((file) => ({
      file,
      status: 'pending' as const,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);

    await Promise.all(
      newUploadedFiles.map(async (uploadedFile) => {
        try {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: 'uploading' } : f,
            ),
          );

          const createdBlobs = await handleUploadToCollectionAndReturn(
            [uploadedFile.file],
            selectedCollectionId,
          );

          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: 'success' } : f,
            ),
          );

          if (createdBlobs && createdBlobs.length > 0) {
            const createdBlob = createdBlobs[0];
            onSelectionChange([...selectedBlobIds, createdBlob.id]);

            // Animate out after success
            setTimeout(() => {
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === uploadedFile.id ? { ...f, isRemoving: true } : f,
                ),
              );
              setTimeout(() => {
                setUploadedFiles((prev) =>
                  prev.filter((f) => f.id !== uploadedFile.id),
                );
              }, 300);
            }, 1000);
          }

          await refetchBlobs();
        } catch {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: 'error' } : f,
            ),
          );
        }
      }),
    );
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files) {
      await handleUploadFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await handleUploadFiles(Array.from(e.dataTransfer.files));
  };

  const removeUploadedFile = (id: string) => {
    setUploadedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isRemoving: true } : f)),
    );
    setTimeout(() => {
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    }, 300);
  };

  // Preview loading
  useEffect(() => {
    if (!enablePreview) return undefined;
    let objectUrl: string | null = null;
    let isMounted = true;

    const loadPreview = async () => {
      if (!previewBlob?.download_url) {
        setPreviewUrl(null);
        setPreviewError(null);
        setIsPreviewLoading(false);
        return;
      }
      setIsPreviewLoading(true);
      setPreviewError(null);
      try {
        const response = await fetch(previewBlob.download_url);
        if (!response.ok) throw new Error('Failed to load preview');
        const fileBlob = await response.blob();
        objectUrl = URL.createObjectURL(fileBlob);
        if (isMounted) setPreviewUrl(objectUrl);
      } catch {
        if (isMounted) {
          setPreviewError('Unable to load document preview');
          setPreviewUrl(null);
        }
      } finally {
        if (isMounted) setIsPreviewLoading(false);
      }
    };

    loadPreview();
    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewBlob, enablePreview]);

  return (
    <div className={cn('flex flex-col h-full min-h-0', className)}>
      {/* Tab switcher (only when upload enabled) */}
      {enableUpload && (
        <div className="flex-shrink-0 mb-3">
          <div className="flex gap-0.5 p-0.5 bg-zinc-100/80 rounded-lg w-full">
            {(['select', 'upload'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2',
                  activeTab === tab
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                {tab === 'select' ? (
                  <>
                    <FolderIcon className="size-4" />
                    Select Existing
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="size-4" />
                    Upload New
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter (only in select mode) */}
      {activeTab === 'select' && (
        <div className="flex-shrink-0 mb-3 space-y-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 text-sm bg-zinc-50 border-0 rounded-xl placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all"
            />
          </div>
          {collectionOptions.length > 1 && (
            <div className="relative">
              <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
              <select
                value={selectedCollectionId}
                onChange={(e) => setSelectedCollectionId(e.target.value)}
                className="w-full h-9 pl-10 pr-8 text-sm bg-zinc-50/70 border-0 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all text-zinc-600"
              >
                {collectionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
            </div>
          )}
        </div>
      )}

      {/* Main content area */}
      {activeTab === 'select' ? (
        /* Document List */
        <div
          className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1"
          role="listbox"
          aria-label="Documents"
          aria-multiselectable={multiSelect}
        >
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="size-5 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-600" />
            </div>
          )}

          {!isLoading && filteredBlobs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FolderIcon className="size-8 text-zinc-200 mb-2" />
              <p className="text-sm text-zinc-500">
                {searchQuery ? 'No matches found' : 'No documents available'}
              </p>
              {enableUpload && !searchQuery && (
                <button
                  type="button"
                  onClick={() => setActiveTab('upload')}
                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Upload a document
                </button>
              )}
            </div>
          )}

          {!isLoading && filteredBlobs.length > 0 && (
            <div className="space-y-1">
              {filteredBlobs.map((blob, index) => (
                <DocumentItem
                  key={blob.id}
                  blob={blob}
                  isSelected={selectedBlobIds.includes(blob.id)}
                  onToggle={(shiftKey) =>
                    handleToggleSelect(blob.id, index, shiftKey)
                  }
                  multiSelect={multiSelect}
                  showPreviewButton={enablePreview}
                  onPreview={() => setPreviewBlob(blob)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Upload Area */
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {/* Folder selector for upload destination */}
          {collectionOptions.length > 1 && (
            <div className="flex-shrink-0">
              <span className="text-xs font-medium text-zinc-500 mb-1.5 block">
                Upload to folder
              </span>
              <div className="relative">
                <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
                <select
                  aria-label="Upload destination folder"
                  value={selectedCollectionId}
                  onChange={(e) => setSelectedCollectionId(e.target.value)}
                  className="w-full h-9 pl-10 pr-8 text-sm bg-zinc-50/70 border-0 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all text-zinc-600"
                >
                  {collectionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Upload queue */}
          {uploadedFiles.length > 0 && (
            <div className="flex-shrink-0 space-y-1.5 max-h-32 overflow-y-auto">
              {uploadedFiles.map((uploadedFile) => (
                <UploadQueueItem
                  key={uploadedFile.id}
                  uploadedFile={uploadedFile}
                  onRemove={() => removeUploadedFile(uploadedFile.id)}
                />
              ))}
            </div>
          )}

          {/* Dropzone */}
          <div
            className={cn(
              'flex-1 min-h-[160px] border-2 border-dashed rounded-xl transition-all',
              isDragging
                ? 'border-indigo-400 bg-indigo-50/50 scale-[0.99]'
                : 'border-zinc-200 hover:border-zinc-300',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="h-full flex flex-col items-center justify-center p-6">
              <CloudArrowUpIcon
                className={cn(
                  'size-10 mb-3 transition-colors',
                  isDragging ? 'text-indigo-500' : 'text-zinc-300',
                )}
              />
              <p className="text-sm font-medium text-zinc-600 mb-1">
                {isDragging ? 'Drop files here' : 'Drag & drop files'}
              </p>
              <p className="text-xs text-zinc-400 mb-3">or</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                Browse Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                multiple
                accept={acceptFileTypes}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Selection summary with document names */}
      {multiSelect && selectedBlobs.length > 0 && (
        <div className="flex-shrink-0 mt-3 p-3 bg-indigo-50/80 rounded-xl border border-indigo-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-indigo-800">
              {selectedBlobs.length} Selected
            </span>
            <button
              type="button"
              onClick={() => onSelectionChange([])}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Clear all
            </button>
          </div>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {selectedBlobs.map((blob) => (
              <div
                key={blob.id}
                className="flex items-center gap-2 group text-xs"
              >
                <DocumentIcon className="size-3.5 text-indigo-400 shrink-0" />
                <span className="text-indigo-700 truncate flex-1">
                  {getBlobDisplayName(blob)}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveSelection(blob.id)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-indigo-200 rounded transition-all"
                  aria-label={`Remove ${getBlobDisplayName(blob)}`}
                >
                  <XMarkIcon className="size-3.5 text-indigo-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm button */}
      {showConfirmButton && selectedBlobIds.length > 0 && (
        <div className="flex-shrink-0 pt-3 mt-3 border-t border-zinc-100">
          <Button
            onClick={onConfirm}
            color="indigo"
            className="w-full shadow-sm"
          >
            Add{' '}
            {multiSelect && selectedBlobIds.length > 1
              ? `${selectedBlobIds.length} Documents`
              : 'Document'}
          </Button>
        </div>
      )}

      {/* Preview Dialog */}
      {enablePreview && (
        <Dialog
          open={!!previewBlob}
          onClose={() => setPreviewBlob(null)}
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        >
          <DialogTitle>{previewDialogTitle}</DialogTitle>
          <div className="mt-4 h-[70vh]">
            {isPreviewLoading && (
              <div className="h-full flex items-center justify-center text-sm text-zinc-500">
                <div className="flex flex-col items-center gap-3">
                  <div className="size-6 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-600" />
                  <span>Loading preview...</span>
                </div>
              </div>
            )}
            {!isPreviewLoading && previewError && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <DocumentIcon className="size-12 text-zinc-200 mx-auto mb-3" />
                  <p className="text-sm text-red-500">{previewError}</p>
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        previewBlob?.download_url,
                        '_blank',
                        'noopener',
                      )
                    }
                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Download instead
                  </button>
                </div>
              </div>
            )}
            {!isPreviewLoading && previewUrl && (
              <iframe
                title="Document preview"
                src={`${previewUrl}#toolbar=0`}
                className="w-full h-full rounded-lg border border-zinc-200"
              />
            )}
          </div>
          {previewBlob && (
            <div className="mt-3 flex items-center justify-between text-sm text-zinc-600">
              <div className="flex items-center gap-2 min-w-0">
                <span className="uppercase text-[10px] font-semibold text-zinc-400 shrink-0">
                  {getFileExtension(previewBlob)}
                </span>
                <span className="truncate">
                  {getBlobDisplayName(previewBlob)}
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  window.open(previewBlob.download_url, '_blank', 'noopener')
                }
                className="text-indigo-600 hover:text-indigo-800 font-medium shrink-0 ml-4"
              >
                Open in new tab
              </button>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
};

// Upload queue item
interface UploadQueueItemProps {
  uploadedFile: UploadedFile;
  onRemove: () => void;
}

const UploadQueueItem: React.FC<UploadQueueItemProps> = ({
  uploadedFile,
  onRemove,
}) => {
  const statusStyles = {
    pending: 'border-zinc-200 bg-zinc-50',
    uploading: 'border-indigo-200 bg-indigo-50/50',
    success: 'border-emerald-200 bg-emerald-50',
    error: 'border-red-200 bg-red-50',
  };

  const statusIcon = {
    pending: <CloudArrowUpIcon className="size-4 text-zinc-400" />,
    uploading: (
      <div className="size-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
    ),
    success: <CheckIcon className="size-4 text-emerald-600" />,
    error: <XMarkIcon className="size-4 text-red-500" />,
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-300',
        statusStyles[uploadedFile.status],
        uploadedFile.isRemoving && 'opacity-0 scale-95 -translate-x-2',
      )}
    >
      {statusIcon[uploadedFile.status]}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-800 truncate">
          {uploadedFile.file.name}
        </p>
        <p className="text-xs text-zinc-500">
          {formatFileSize(uploadedFile.file.size)}
        </p>
      </div>
      {uploadedFile.status !== 'uploading' && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1 hover:bg-zinc-200/50 rounded transition-colors"
        >
          <XMarkIcon className="size-4 text-zinc-400 hover:text-zinc-600" />
        </button>
      )}
    </div>
  );
};

// Document item
interface DocumentItemProps {
  blob: BlobType;
  isSelected: boolean;
  onToggle: (shiftKey: boolean) => void;
  multiSelect: boolean;
  showPreviewButton?: boolean;
  onPreview?: () => void;
}

const DocumentItem = React.memo<DocumentItemProps>(
  ({
    blob,
    isSelected,
    onToggle,
    multiSelect,
    showPreviewButton,
    onPreview,
  }) => {
    const handleClick = (e: React.MouseEvent) => {
      onToggle(e.shiftKey);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle(e.shiftKey);
      }
    };

    return (
      <div
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full px-3 py-2.5 rounded-xl text-left transition-all group cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
          isSelected
            ? 'bg-indigo-50 ring-1 ring-indigo-200'
            : 'hover:bg-zinc-50 active:bg-zinc-100',
        )}
      >
        <div className="flex items-center gap-3">
          {/* Selection indicator */}
          <div
            className={cn(
              'size-5 flex items-center justify-center shrink-0 transition-all',
              multiSelect ? 'rounded' : 'rounded-full',
              isSelected
                ? 'bg-indigo-600'
                : 'border-2 border-zinc-200 group-hover:border-zinc-300',
            )}
          >
            {isSelected && <CheckIcon className="size-3 text-white" />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <span
              className={cn(
                'text-xs font-medium truncate block',
                isSelected ? 'text-indigo-900' : 'text-zinc-800',
              )}
            >
              {getBlobDisplayName(blob)}
            </span>
            <div
              className={cn(
                'flex items-center gap-1 text-[10px] mt-0.5',
                isSelected ? 'text-indigo-600/70' : 'text-zinc-400',
              )}
            >
              <span className="uppercase text-[9px] font-semibold">
                {getFileExtension(blob)}
              </span>
              <span>·</span>
              <span>{formatFileSize(blob.file_size)}</span>
            </div>
          </div>

          {/* Preview button */}
          {showPreviewButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPreview?.();
              }}
              className={cn(
                'text-xs font-medium px-2 py-1 rounded-lg transition-all shrink-0',
                'opacity-0 group-hover:opacity-100 focus:opacity-100',
                isSelected
                  ? 'text-indigo-600 hover:bg-indigo-100'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100',
              )}
            >
              Preview
            </button>
          )}
        </div>
      </div>
    );
  },
);

DocumentItem.displayName = 'DocumentItem';
